// Interpreta o objeto cru devolvido por dirs.list()/mtd.list() (getAll() do
// backend, ver sir-prod/src/dir/Model.js e mtd/Model.js) em grupos de porta
// pra exibição no diagrama de conexão. Mesma leitura de campos que
// Dirs/View.vue (organizeDirs/organizeMtd) já faz pra editar o dispositivo
// -- aqui só extraímos { posição, nome do equipamento } por grupo, sem os
// prefixos "h"/"p" que aquela tela usa pra desambiguar <option> de formulário.

// D (hidrômetro) e P (sensor de pulso) dividem a mesma entrada física --
// DIGITAL_CAPACITY é o total de pinos, nunca os dois preenchidos na mesma
// posição (Dirs/components/DirForm.vue).
function buildDigitalSlots(item, capacity) {
  const slots = [];
  for (let pos = 1; pos <= (capacity || 0); pos++) {
    const hydroName = item[`EQUIP_ON_D${pos}`];
    const pulseName = item[`EQUIP_ON_P${pos}`];
    slots.push({
      position: pos,
      equipmentName: hydroName || pulseName || null,
      subtype: hydroName ? "Hidrômetro" : pulseName ? "Pulso" : null,
    });
  }
  return slots;
}

function buildSimpleSlots(item, prefix, capacity) {
  const slots = [];
  for (let pos = 1; pos <= (capacity || 0); pos++) {
    slots.push({
      position: pos,
      equipmentName: item[`EQUIP_ON_${prefix}${pos}`] || null,
    });
  }
  return slots;
}

// I2C e SDI-12 têm um primeiro slot sem sufixo numérico (campo "I2C"/"SDI12"
// na CAD_DIR) seguido de I2C1..I2C7/SDI12I1..SDI12I7 -- mesma numeração de
// posição 1..N usada em Dirs/View.vue:organizeDirs.
function buildI2cSlots(item, capacity) {
  const slots = [];
  for (let pos = 1; pos <= (capacity || 0); pos++) {
    const key = pos === 1 ? "EQUIP_ON_I2C" : `EQUIP_ON_I2C${pos - 1}`;
    slots.push({ position: pos, equipmentName: item[key] || null });
  }
  return slots;
}

function buildSdi12Slots(item, capacity) {
  const slots = [];
  for (let pos = 1; pos <= (capacity || 0); pos++) {
    const key = pos === 1 ? "EQUIP_ON_SDI12" : `EQUIP_ON_SDI12I${pos - 1}`;
    slots.push({ position: pos, equipmentName: item[key] || null });
  }
  return slots;
}

function buildSingleSlot(item, field) {
  return [{ position: 1, equipmentName: item[`EQUIP_ON_${field}`] || null }];
}

// Grupos de um DIR/FIR/REP (CAD_DIR) -- capacidade real vem de
// CAD_MODEL_DIR (VALVE_CAPACITY/DIGITAL_CAPACITY/SENSOR_CAPACITY/
// I2C_CAPACITY/SDI12_CAPACITY, já resolvidos por dir/Model.js:getAll());
// MODBUS e ON/OFF não têm coluna própria no modelo, são fixos em 8 no
// hardware (mesma premissa de Dirs/View.vue:organizeDirs).
export function buildDirPortGroups(item) {
  return [
    { key: "valve", label: "Saídas", slots: buildSimpleSlots(item, "O", item.VALVE_CAPACITY) },
    { key: "digital", label: "Hidrômetro / Pulso", slots: buildDigitalSlots(item, item.DIGITAL_CAPACITY) },
    { key: "analog", label: "Entradas analógicas", slots: buildSimpleSlots(item, "A", item.SENSOR_CAPACITY) },
    { key: "i2c", label: "I2C", slots: buildI2cSlots(item, item.I2C_CAPACITY) },
    { key: "sdi12", label: "SDI-12", slots: buildSdi12Slots(item, item.SDI12_CAPACITY) },
    { key: "modbusPump", label: "Modbus (bomba)", slots: buildSimpleSlots(item, "M", 8) },
    { key: "modbusSensor", label: "Modbus (sensor)", slots: buildSimpleSlots(item, "SM", 8) },
    { key: "onoff", label: "Entradas on/off", slots: buildSimpleSlots(item, "L", 8) },
    { key: "emr", label: "EMR", slots: buildSingleSlot(item, "EMR") },
    { key: "pir", label: "PIR", slots: buildSingleSlot(item, "PIR") },
  ].filter((group) => group.slots.length > 0);
}

// CAD_MTD: subconjunto reduzido de portas (sem modbus/analógica/EMR/PIR) e
// sem campos *_CAPACITY no getAll() (sir-prod/src/mtd/Model.js) -- usa os
// tamanhos fixos do hardware (mesma premissa de Dirs/View.vue:organizeMtd).
export function buildMtdPortGroups(item) {
  return [
    { key: "valve", label: "Saídas", slots: buildSimpleSlots(item, "O", 4) },
    { key: "digital", label: "Hidrômetro / Pulso", slots: buildDigitalSlots(item, 4) },
    { key: "i2c", label: "I2C", slots: buildI2cSlots(item, 4) },
    { key: "sdi12", label: "SDI-12", slots: buildSdi12Slots(item, 4) },
    { key: "onoff", label: "Entradas on/off", slots: buildSimpleSlots(item, "L", 4) },
  ].filter((group) => group.slots.length > 0);
}

// Usado pelo filtro "ocultar dispositivos sem conexões" -- true se nenhuma
// porta do dispositivo (em nenhum grupo) tem equipamento associado.
export function isDeviceEmpty(item, isMtd) {
  const groups = isMtd ? buildMtdPortGroups(item) : buildDirPortGroups(item);
  return !groups.some((group) => group.slots.some((slot) => slot.equipmentName));
}

// Usado pelo filtro de quantidade de atuadores/sensores (issue #460).
// "valve" (saídas) e "modbusPump" (bomba via Modbus) contam como atuador; o
// restante conta como sensor, exceto o "Hidrômetro" dentro de "digital" --
// vazão é medida por um CAD_HYDRO, entidade própria, não um CAD_SENSOR, então
// não entra nem como atuador nem como sensor. O "Pulso" da mesma porta física
// (subtype alternativo do grupo "digital") é sensor normalmente.
const ACTUATOR_GROUP_KEYS = ["valve", "modbusPump"];

export function countBySlotKind(item, isMtd) {
  const groups = isMtd ? buildMtdPortGroups(item) : buildDirPortGroups(item);
  let actuators = 0;
  let sensors = 0;
  groups.forEach((group) => {
    const occupied = group.slots.filter((slot) => slot.equipmentName);
    if (ACTUATOR_GROUP_KEYS.includes(group.key)) {
      actuators += occupied.length;
    } else if (group.key === "digital") {
      sensors += occupied.filter((slot) => slot.subtype === "Pulso").length;
    } else {
      sensors += occupied.length;
    }
  });
  return { actuators, sensors };
}

// Achata a lista de dispositivos numa linha por porta OCUPADA -- pra
// exportação em planilha, só o que está de fato conectado (não é um mapa
// de capacidade, é o "o que tem plugado onde" que a tela já mostra).
export function buildExportRows(devices) {
  const rows = [];
  devices.forEach((device) => {
    const groups = device._isMtd ? buildMtdPortGroups(device) : buildDirPortGroups(device);
    groups.forEach((group) => {
      group.slots
        .filter((slot) => slot.equipmentName)
        .forEach((slot) => {
          rows.push({
            DISPOSITIVO: device.NAME || device.MODEL_NAME,
            ADDR: device.ADDR,
            MODELO: device.MODEL_NAME,
            GRUPO: group.label,
            PORTA: slot.position,
            EQUIPAMENTO: slot.equipmentName,
          });
        });
    });
  });
  return rows;
}

// Texto pesquisável do dispositivo -- nome/ADDR/modelo do próprio
// dispositivo mais o nome de qualquer equipamento conectado em qualquer
// porta, pra a busca achar tanto "onde está o DIR X" quanto "em qual
// dispositivo está plugada a bomba Y".
export function deviceSearchText(item, isMtd) {
  const groups = isMtd ? buildMtdPortGroups(item) : buildDirPortGroups(item);
  const equipmentNames = groups.flatMap((group) =>
    group.slots.map((slot) => slot.equipmentName).filter(Boolean)
  );
  return [item.NAME, item.ADDR, item.MODEL_NAME, ...equipmentNames]
    .filter((value) => value !== null && value !== undefined)
    .join(" ")
    .toLowerCase();
}
